import { useEffect } from 'react';

/**
 * StaffLayout
 *
 * Root layout shell for all staff-facing pages. Provides a sticky header,
 * content header row, optional right sidebar, and a scrollable content area.
 *
 * @param {object} props
 * @param {string} [props.pageTitle]
 * @param {React.ReactNode} [props.headerIcon]
 * @param {React.ReactNode} [props.headerTitle]
 * @param {React.ReactNode} [props.backButton]
 * @param {React.ReactNode} [props.contentHeaderTitle]
 * @param {React.ReactNode} [props.contentHeaderAction]
 * @param {React.ReactNode} [props.customContentHeader]
 * @param {string} [props.contentClassName]
 * @param {React.ReactNode} [props.rightSidebar]
 * @param {React.ReactNode} [props.footer]
 * @param {boolean} [props.showChildrenTopPadding]
 * @param {boolean} [props.showRightSidebarTopPadding]
 * @param {React.ReactNode} props.children
 * @param {boolean} [props.scrollable]
 */
export default function StaffLayout({
    pageTitle = 'คลินิกเวชกรรม ใกล้หมอ',
    headerIcon = null,
    headerTitle = null,
    backButton = null,
    contentHeaderTitle = null,
    contentHeaderAction = null,
    customContentHeader = null,
    contentClassName = null,
    rightSidebar = null,
    footer = null,
    showChildrenTopPadding = true,
    showRightSidebarTopPadding = true,
    children,
    scrollable = true,
}) {
    useEffect(() => {
        document.title = pageTitle || 'คลินิกเวชกรรม ใกล้หมอ';
    }, [pageTitle]);

    function buildHeader() {
        return <header className="sticky top-0 z-20 p-6 gap-5 border-b bg-main-background-system-default flex items-center flex-row">
            <div className="flex gap-3 items-center">
                {headerIcon}
                <h1 className="font-semibold text-headline text-main-typography-system-default">
                    {headerTitle}
                </h1>
            </div>
            <div className="flex h-[48px]">{/* Placeholder for MedicaionRequestChangeBadge */}</div>
        </header>;
    }

    function buildContentHeader() {
        return <div>
            {customContentHeader ? (
                customContentHeader
            ) : (
                <div className="flex flex-row items-center justify-between mt-3 py-5 px-6 gap-5 border-b bg-main-background-system-default">
                    <div className="flex flex-row items-center gap-5">
                        {backButton}
                        <h2 className="font-semibold text-title text-main-typography-system-default">
                            {contentHeaderTitle}
                        </h2>
                    </div>
                    {contentHeaderAction}
                </div>
            )}
        </div>
    }

    function buildContent() {
        const scrollableClass = scrollable ? 'custom-scrollbar overflow-y-auto' : 'overflow-y-hidden';
        const topPaddingClass = showChildrenTopPadding ? 'pt-5' : '';
        const contentClass = contentClassName || '';

        if (!rightSidebar) {
            const className = `
                flex flex-1 min-h-0 pl-6 pr-2 mr-3
                ${scrollableClass}
                ${topPaddingClass} ${contentClass}
            `;

            return <div
                className={className}
                style={{ scrollbarGutter: "stable" }}>
                <div className="w-full pr-2">
                    {children}
                </div>
            </div>;
        }

        const leftClassName = `
            flex flex-1 min-h-0 pl-6 
            ${topPaddingClass} ${contentClass}
            ${scrollableClass}
        `;
        const rightSidebarTopPaddingClass = showRightSidebarTopPadding === false ? '' : topPaddingClass;

        return (
            <div className="flex flex-1 min-h-0 overflow-hidden">
                <div className={leftClassName} style={{ scrollbarGutter: "stable" }}>
                    <div className="w-full pr-2">
                        {children}
                    </div>
                </div>
                <aside className={`flex-shrink-0 min-h-0 pl-2 ${rightSidebarTopPaddingClass}`}>
                    {rightSidebar}
                </aside>
            </div>
        );
    }

    function buildFooter() {
        return footer && <footer className="mx-6 mt-6 mb-5">{footer}</footer>;
    }

    return (
        <div className="flex-1 min-w-0 flex flex-col h-screen min-h-0 overflow-hidden">
            {buildHeader()}
            <main className="flex-1 flex flex-col min-h-0 overflow-hidden bg-main-background-system-default-dim">
                {buildContentHeader()}
                {buildContent()}
                {buildFooter()}
            </main>
        </div>
    );
}
